"use client"

import { useEffect, useState } from "react"

import { hasPersistedSupabaseSession } from "@/components/marketing/use-marketing-auth"

export type ArticleEntitlement = {
  /** True or false only after the server has verified an entitlement answer. */
  unlimited: boolean | null
  /** The answer has settled. False on the server and on the first client
   *  render, and while the request for a signed-in reader is in flight. Null
   *  after a failed answer means unknown, not metered. */
  ready: boolean
}

const SIGNED_OUT: ArticleEntitlement = { unlimited: false, ready: true }
const PENDING: ArticleEntitlement = { unlimited: null, ready: false }

/**
 * Whether the reader may pass the article meter, asked of the server.
 *
 * SIGNED IN IS NOT THE QUESTION. It used to be, and that was a bypass: a wall
 * that stands down for any signed-in reader hands every page it walls to a
 * free account, and anyone can make one. The subscription, the community
 * membership and a comped account are the three doors, and only the server can
 * see them (web/lib/access.ts `hasUnlimitedReading`).
 *
 * A SIGNED-OUT READER MAKES NO REQUEST, which is the property that keeps this
 * clear of the library's rankings and its bill alike: no persisted Supabase
 * session cookie means signed out for certain, so paid traffic, a first-time
 * reader and every crawler resolve locally and the article HTML they are
 * served is unchanged. Nothing here reads a user agent.
 */
export function useArticleEntitlement(): ArticleEntitlement {
  const [state, setState] = useState<ArticleEntitlement>(PENDING)

  useEffect(() => {
    let active = true
    const controller = new AbortController()

    // ONE asynchronous path, deliberately. The signed-out answer is known
    // without a network call, but resolving it through a promise rather than
    // setting state inline keeps this effect free of a synchronous setState,
    // which React flags as a cascading render. No fetch is constructed for a
    // signed-out reader either way, which is the property that matters.
    const answer: Promise<boolean | null> = hasPersistedSupabaseSession()
      ? fetch("/api/articles/entitlement", {
          signal: controller.signal,
          credentials: "same-origin",
          headers: { accept: "application/json" },
        })
          .then((res) => (res.ok ? (res.json() as Promise<{ unlimited?: unknown }>) : null))
          .then((body) => (typeof body?.unlimited === "boolean" ? body.unlimited : null))
      : Promise.resolve(SIGNED_OUT.unlimited)

    answer
      .then((unlimited) => {
        if (active) setState({ unlimited, ready: true })
      })
      .catch(() => {
        if (active) setState({ unlimited: null, ready: true })
      })

    return () => {
      active = false
      controller.abort()
    }
  }, [])

  return state
}
